<script setup lang="ts">
import type { Schemas } from '#shopware'
import type { PromotionData } from '~/composables/usePromotion'
import { useClipboard } from '@vueuse/core'

const props = defineProps<{
    slide: Schemas['HeroSlide']
    index: number
}>()

const heroSlideEl = ref<HTMLDivElement | null>(null)

const promotionData: PromotionData | null = props.slide.promotion_name && props.slide.promotion_id && props.slide.creative_name && props.slide.creative_slot
    ? {
        promotion_name: props.slide.promotion_name,
        promotion_id: props.slide.promotion_id,
        creative_name: props.slide.creative_name,
        creative_slot: props.slide.creative_slot,
    }
    : null

const { selectPromotion, computedPromotionData } = usePromotionObserver(heroSlideEl, promotionData)

const slideVersionNoBgImage = computed(() => props.slide.version === '2')

const bgColorClass = computed(() => `bg-${props.slide.bgColor}`)

const useDarkTextColor = computed(() => {
    return props.slide.bgColor === 'accent' || props.slide.bgColor === 'base-300'
})

const buttonClass = computed(() => {
    return props.slide.buttonType === 'accent' ? 'btn-accent' : 'btn-light'
})

const isHeadlineAboveImage = computed(() => props.slide.headlineMobile === 'top')

const textRef = ref<HTMLElement | null>(null)
const { copy, copied, isSupported } = useClipboard()

function copyToClipboard() {
    const text = textRef.value?.innerText
    if (text && isSupported.value) {
        copy(text)
    }
}

function onHeroElClick(event: MouseEvent) {
    if (computedPromotionData?.value) selectPromotion(computedPromotionData.value, event)
}

function hasContent(input: string | null | undefined): input is string {
    return input != null && input.trim() !== ''
}
</script>

<template>
    <!-- eslint-disable-next-line vuejs-accessibility/no-static-element-interactions vuejs-accessibility/click-events-have-key-events -->
    <div
        ref="heroSlideEl"
        class="h-[650px] sm:h-[900px] md:h-[1029px] lg:h-[684px] w-full aspect-[2/3] md:aspect-[12/5] relative"
        :class="{ [bgColorClass] : slideVersionNoBgImage }"
        @click="onHeroElClick($event)"
    >
        <div class="flex m-auto justify-center lg:px-16 xl:px-0">
            <div class="flex flex-col lg:flex-row px-6 pt-6 lg:pt-14 lg:px-0 gap-6 lg:gap-14 lg:max-w-[1154px]">
                <MiscImage
                    v-if="slideVersionNoBgImage"
                    :src="slide.image?.path"
                    :alt="slide.image?.translated?.alt"
                    :media-width="slide.image?.metaData?.width"
                    :media-height="slide.image?.metaData?.height"
                    :lazy-load="index > 0"
                    :preload="index === 0 ? { fetchPriority: 'high' } : false"
                    width="1940"
                    sizes="592px sm:714px md:714px lg:639px xl:639px 2xl:1940px"
                    class="aspect-[15/11] object-cover md:max-w-[639px] min-[834px]:max-w-[714px] lg:max-w-[639px] col-start-2 col-end-4"
                    :class="[isHeadlineAboveImage ? 'order-3' : 'order-2']"
                />
                <div class="contents lg:flex lg:flex-col lg:py-16 lg:max-w-[460px] lg:gap-5">
                    <div
                        v-if="hasContent(slide.headline1)"
                        class="heading font-normal text-sale-content uppercase font-fjalla-one"
                        :class="[
                            slideVersionNoBgImage ? 'text-4xl md:text-6.5xl' : 'text-8xl md:text-10xl',
                            isHeadlineAboveImage ? 'block lg:hidden order-2' : 'hidden order-3',
                            { 'text-secondary-content': useDarkTextColor }
                        ]"
                    >
                        {{ slide.headline1 }}
                    </div>

                    <div v-if="hasContent(slide.countdownText)" class="flex md:block">
                        <div
                            class="text-base lg:text-sm font-semibold"
                            :class="[useDarkTextColor ? 'text-secondary-content' : 'text-base-100']"
                        >
                            {{ slide.countdownText }}
                            <span aria-hidden="true" class="md:hidden">&nbsp;</span>
                        </div>

                        <ClientOnly v-if="slide.countdownEnd">
                            <StructureElementHeroSliderBanderoleCountdown
                                :date="slide.countdownEnd"
                                :type="slide.countdownType ?? undefined"
                                class="flex justify-start items-center gap-1 md:gap-2 capitalize"
                                :class="{ 'text-secondary-content': useDarkTextColor }"
                                v-bind="$attrs"
                                :increased-font-size="true"
                                :is-simple="true"
                            />
                        </ClientOnly>
                    </div>

                    <div class="flex flex-col gap-3" :class="[isHeadlineAboveImage ? 'order-3' : 'order-4']">
                        <div
                            v-if="hasContent(slide.headline1)"
                            class="heading font-normal text-sale-content uppercase font-fjalla-one"
                            :class="[
                                slideVersionNoBgImage ? 'text-4xl md:text-6.5xl' : 'text-8xl md:text-10xl',
                                isHeadlineAboveImage ? 'hidden lg:block' : 'block',
                                { 'text-secondary-content': useDarkTextColor }
                            ]"
                        >
                            {{ slide.headline1 }}
                        </div>

                        <div
                            v-if="hasContent(slide.subtitleLong)"
                            class="subtitle text-sm font-semibold"
                            :class="[
                                useDarkTextColor ? 'text-secondary-content' : 'text-base-100',
                                { 'md:text-lg': !slideVersionNoBgImage }
                            ]"
                        >
                            {{ slide.subtitleLong }}
                        </div>
                    </div>

                    <div
                        v-if="hasContent(slide.promotionCode)"
                        role="button"
                        tabindex="0"
                        class="font-normal flex gap-2 items-center order-5"
                        :class="[
                            slide.promotionCodeSize === 'small' ? 'text-xs' : 'text-lg',
                            useDarkTextColor ? 'text-secondary-content' : 'text-base-100',
                            { 'md:text-lg': !slideVersionNoBgImage }
                        ]"
                        @click.prevent="copyToClipboard"
                        @keydown.prevent.enter="copyToClipboard"
                    >
                        <span class="uppercase">{{ $t('misc.code') }}:</span>
                        <span ref="textRef" class="font-bold">{{ slide.promotionCode }}</span>
                        <MiscIcon v-if="!copied" name="clipboard" :size="slide.promotionCodeSize === 'small' ? 'xs': 'sm'" />
                        <MiscIcon v-else name="check" :size="slide.promotionCodeSize === 'small' ? 'xs': 'sm'" />
                    </div>

                    <button
                        v-if="hasContent(slide.buttonText) && hasContent(slide.buttonTarget)"
                        type="button"
                        class="btn slide-button btn-md w-60 px-4 rounded-xs order-6"
                        :class="[buttonClass]"
                    >
                        {{ slide.buttonText }}
                    </button>
                </div>
            </div>
        </div>
    </div>
</template>
